import { bandDatasetName, unpackCmi } from "./goesColor.js";
import { indexToLatLon } from "./goesGrid.js";

function scalarAttr(dataset, name) {
  const attr = dataset?.attrs?.[name];
  if (!attr) return null;
  const value = attr.value;
  if (ArrayBuffer.isView(value)) return value.length ? value[0] : null;
  if (Array.isArray(value)) return value.length ? value[0] : null;
  return value;
}

function numberAttr(dataset, name) {
  const value = Number(scalarAttr(dataset, name));
  return Number.isFinite(value) ? value : null;
}

// The axes hold cell centers; angle0 is the outer edge of the first cell, so
// the 0.5, 1 and 2 km grids of one sector nest exactly.
function axisAngles(dataset) {
  const raw0 = Number(dataset.slice([[0, 1]])[0]);
  const raw1 = Number(dataset.slice([[1, 2]])[0]);
  const scale = numberAttr(dataset, "scale_factor") ?? 1;
  const offset = numberAttr(dataset, "add_offset") ?? 0;
  const angleStep = (raw1 - raw0) * scale;
  return {
    angle0: (raw0 * scale) + offset - (angleStep / 2),
    angleStep,
  };
}

export function readProjection(file) {
  const projDs = file.get("goes_imager_projection");
  const xds = file.get("x");
  const yds = file.get("y");
  if (!projDs || !xds || !yds) throw new Error("GOES file is missing the fixed grid");
  const xAxis = axisAngles(xds);
  const yAxis = axisAngles(yds);
  const proj = {
    lon0: numberAttr(projDs, "longitude_of_projection_origin"),
    req: numberAttr(projDs, "semi_major_axis"),
    rpol: numberAttr(projDs, "semi_minor_axis"),
    height: numberAttr(projDs, "perspective_point_height"),
    xAngle0: xAxis.angle0,
    xAngleStep: xAxis.angleStep,
    yAngle0: yAxis.angle0,
    yAngleStep: yAxis.angleStep,
    nx: xds.shape[0],
    ny: yds.shape[0],
  };
  if (![proj.lon0, proj.req, proj.rpol, proj.height].every(Number.isFinite)) {
    throw new Error("GOES projection attributes are missing");
  }
  return proj;
}

// Only the requested bands are read. HDF5 fetches each band's chunks on its own,
// so a single-band product costs about 1/16 of the MCMIP file. A single-band
// CMIP file names its one band "CMI".
export function readBand(file, band, { x0, x1, y0, y1, stride = 1 }) {
  const dataset = file.get(bandDatasetName(band)) || file.get("CMI");
  if (!dataset) throw new Error(`Missing ${bandDatasetName(band)}`);
  const raw = dataset.slice([[y0, y1, stride], [x0, x1, stride]]);
  return unpackCmi(
    raw,
    numberAttr(dataset, "scale_factor") ?? 1,
    numberAttr(dataset, "add_offset") ?? 0,
    numberAttr(dataset, "_FillValue"),
    scalarAttr(dataset, "_Unsigned"),
  );
}

export function cropCorners({ x0, x1, y0, y1 }, proj) {
  return [
    indexToLatLon(x0, y0, proj),
    indexToLatLon(x1 - 1, y0, proj),
    indexToLatLon(x0, y1 - 1, proj),
    indexToLatLon(x1 - 1, y1 - 1, proj),
  ].filter(Boolean);
}
